/* =========================================================
   KANGRIZ SITE MOTION + ELLA GRID NORMALIZER

   IMPORTANT:
   - Does not change Ella column count
   - Does not change product grid widths
   - Does not change flex-basis
   - Detects real Ella grid cells
   - Controls homepage product-section spacing
   - Controls collection card gaps
   - Keeps product cards equal
   - Aligns Quick Add correctly
   ========================================================= */

(() => {
  const GRID_SELECTOR = [
    '.productGrid',
    '.products-grid',
    '.product-grid',
    '[data-product-grid]'
  ].join(',');

  const CARD_SELECTOR = [
    '.product-item',
    '.product-card',
    '.card-product',
    '.card-wrapper',
    '.grid-view-item'
  ].join(',');

  const MEDIA_SELECTOR = [
    '.card-media',
    '.card-media-wrapper',
    '.card__media',
    '.card-product__media',
    '.card-product__image-wrapper',
    '.product-item__media',
    '.product-item__image-wrapper',
    '.product-image',
    '.product-image-wrapper',
    '.product-item-image',
    '.product-image-container'
  ].join(',');

  const CONTENT_SELECTOR = [
    '.card-information',
    '.card__content',
    '.card-product__content',
    '.card-product__info',
    '.product-details',
    '.product-item-details',
    '.product-item__content',
    '.product-item__info',
    '.product-bottom'
  ].join(',');

  const QUICK_BUTTON_SELECTOR = [
    '.quick-add__submit',
    '.button-ATC',
    '.ajax-add-to-cart',
    '.btn-product',
    '.notify-me',

    '.card-action .button',
    '.card-action .btn',

    '.product-card-action .button',
    '.product-card-action .btn',

    '.quick-add button',

    '.action .button',
    '.action .btn'
  ].join(',');

  const QUICK_WRAP_SELECTOR = [
    '.card-action',
    '.quick-add',
    '.product-card-action',
    '.action',
    '.card-action-wrapper',
    '.product-card__button'
  ].join(',');

  let resizeTimer = null;
  let refreshQueued = false;


  /* =========================================================
     CHECK IF ELEMENT IS A PRODUCT GRID CELL
     ========================================================= */

  const isProductCell = cell => {
    return (
      cell instanceof HTMLElement &&
      !!cell.querySelector(
        'a[href*="/products/"]'
      )
    );
  };


  /* =========================================================
     SECTION REVEAL ANIMATION
     ========================================================= */

  const initSectionMotion = (
    root = document
  ) => {
    const sections = Array.from(
      root.querySelectorAll?.(
        '.shopify-section:not(.shopify-section-group-header-group):not(.shopify-section-group-footer-group)'
      ) || []
    ).filter(
      section =>
        !section.classList.contains(
          'kg-motion-ready'
        )
    );

    if (!sections.length) return;

    const reduced =
      window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

    sections.forEach(section => {
      section.classList.add(
        'kg-motion-ready',
        'kg-section-reveal'
      );
    });

    if (
      reduced ||
      !('IntersectionObserver' in window)
    ) {
      sections.forEach(section => {
        section.classList.add(
          'kg-section-visible'
        );
      });

      return;
    }

    const observer =
      new IntersectionObserver(
        (
          entries,
          currentObserver
        ) => {
          entries.forEach(entry => {
            if (
              !entry.isIntersecting
            ) {
              return;
            }

            entry.target.classList.add(
              'kg-section-visible'
            );

            currentObserver.unobserve(
              entry.target
            );
          });
        },
        {
          threshold: 0.05,
          rootMargin:
            '0px 0px -3% 0px'
        }
      );

    sections.forEach(section => {
      observer.observe(section);
    });
  };


  /* =========================================================
     FIND PRODUCT GRIDS
     ========================================================= */

  const getGrids = (
    root = document
  ) => {
    const grids = [];

    if (
      root instanceof HTMLElement &&
      root.matches(GRID_SELECTOR)
    ) {
      grids.push(root);
    }

    root
      .querySelectorAll?.(
        GRID_SELECTOR
      )
      .forEach(grid => {
        grids.push(grid);
      });

    return [
      ...new Set(grids)
    ];
  };


  /* =========================================================
     HOME PRODUCT SECTION DETECTION
     ========================================================= */

  const tagHomeSection = grid => {
    if (
      !document.body.classList.contains(
        'template-index'
      )
    ) {
      return;
    }

    const section =
      grid.closest(
        '.shopify-section'
      );

    if (section) {
      section.classList.add(
        'kg-home-product-section'
      );
    }

    const block =
      grid.closest(
        [
          '.halo-block',
          '.halo-product-block',
          '.product-block',
          '.section-block',
          '.featured-collection',
          '.product-block-wrapper'
        ].join(',')
      );

    if (block) {
      block.classList.add(
        'kg-home-product-block'
      );
    } else if (section) {
      section.classList.add(
        'kg-home-product-block'
      );
    }
  };


  /* =========================================================
     GET ACTUAL PRODUCT CARD
     ========================================================= */

  const getCardFromCell = cell => {
    if (!cell) return null;

    if (
      cell.matches(
        CARD_SELECTOR
      )
    ) {
      return cell;
    }

    return (
      cell.querySelector(
        CARD_SELECTOR
      ) ||
      cell
    );
  };


  /* =========================================================
     FIND PRODUCT MEDIA BOX
     ========================================================= */

  const getMediaBox = card => {
    const current =
      card.querySelector(
        '.kg-media-box'
      );

    if (current) {
      return current;
    }

    const image =
      card.querySelector(
        'a[href*="/products/"] img'
      ) ||
      card.querySelector('img');

    if (!image) return null;

    let media =
      image.closest(
        MEDIA_SELECTOR
      );

    if (!media) {
      media =
        image.closest(
          'a[href*="/products/"]'
        );
    }

    if (
      !media ||
      !card.contains(media)
    ) {
      return null;
    }

    media.classList.add(
      'kg-media-box'
    );

    return media;
  };


  /* =========================================================
     FIND CARD CONTENT BOX
     ========================================================= */

  const getContentBox = card => {
    const current =
      card.querySelector(
        '.kg-card-content'
      );

    if (current) {
      return current;
    }

    const content =
      card.querySelector(
        CONTENT_SELECTOR
      );

    if (
      content &&
      content !== card
    ) {
      content.classList.add(
        'kg-card-content'
      );

      return content;
    }

    return null;
  };


  /* =========================================================
     FIND QUICK ADD
     ========================================================= */

  const getQuickAdd = card => {
    const existingWrap =
      card.querySelector(
        '.kg-quick-add-wrap'
      );

    const existingButton =
      card.querySelector(
        '.kg-quick-add-button'
      );

    if (
      existingWrap &&
      existingButton
    ) {
      return {
        wrap: existingWrap,
        button: existingButton
      };
    }

    const buttons =
      Array.from(
        card.querySelectorAll(
          QUICK_BUTTON_SELECTOR
        )
      );

    const button =
      buttons.find(
        candidate => {
          return !candidate.closest(
            '.wishlist, .quickview, .compare'
          );
        }
      );

    if (!button) {
      return null;
    }

    const wrap =
      button.closest(
        QUICK_WRAP_SELECTOR
      ) ||
      button.parentElement;

    if (
      !wrap ||
      !card.contains(wrap)
    ) {
      return null;
    }

    wrap.classList.add(
      'kg-quick-add-wrap'
    );

    button.classList.add(
      'kg-quick-add-button'
    );

    return {
      wrap,
      button
    };
  };


  /* =========================================================
     POSITION QUICK ADD INSIDE REAL IMAGE BOX
     ========================================================= */

  const positionQuickAdd = (
    card,
    media,
    quick
  ) => {
    if (
      !card ||
      !media ||
      !quick
    ) {
      return;
    }

    const {
      wrap,
      button
    } = quick;


    card.style.position =
      'relative';

    wrap.style.position =
      'absolute';

    wrap.style.right =
      'auto';

    wrap.style.bottom =
      'auto';


    const isMobile =
      window.innerWidth <= 749;

    const inset =
      isMobile
        ? 8
        : 12;


    const buttonHeight =
      button
        .getBoundingClientRect()
        .height ||
      (
        isMobile
          ? 38
          : 42
      );


    const offsetParent =
      wrap.offsetParent ||
      card;

    const parentRect =
      offsetParent
        .getBoundingClientRect();

    const mediaRect =
      media
        .getBoundingClientRect();


    const width =
      Math.max(
        80,
        mediaRect.width -
        inset * 2
      );


    const left =
      mediaRect.left -
      parentRect.left +
      inset;


    const top =
      mediaRect.bottom -
      parentRect.top -
      buttonHeight -
      inset;


    wrap.style.left =
      `${Math.round(left)}px`;

    wrap.style.top =
      `${Math.round(top)}px`;

    wrap.style.width =
      `${Math.round(width)}px`;
  };


  /* =========================================================
     EQUAL HEIGHT CARDS
     ONLY WITHIN SAME VISUAL ROW
     ========================================================= */

  const equalizeGridRows = cards => {

    cards.forEach(card => {
      card.style.minHeight = '';
    });


    requestAnimationFrame(() => {

      const rows = [];


      cards.forEach(card => {

        const rect =
          card
            .getBoundingClientRect();


        if (
          rect.width < 70 ||
          rect.height < 40
        ) {
          return;
        }


        let row =
          rows.find(group => {

            return (
              Math.abs(
                group.top -
                rect.top
              ) <= 14
            );

          });


        if (!row) {

          row = {
            top: rect.top,
            cards: []
          };

          rows.push(row);

        }


        row.cards.push(card);

      });


      rows.forEach(row => {

        if (
          row.cards.length < 2
        ) {
          return;
        }


        const maxHeight =
          Math.max(
            ...row.cards.map(
              card =>
                Math.ceil(
                  card
                    .getBoundingClientRect()
                    .height
                )
            )
          );


        row.cards.forEach(
          card => {

            card.style.minHeight =
              `${maxHeight}px`;

          }
        );

      });

    });
  };


  /* =========================================================
     SETUP ONE GRID
     ========================================================= */

  const setupGrid = grid => {

    tagHomeSection(grid);


    const cells =
      Array.from(
        grid.children
      ).filter(
        isProductCell
      );


    if (!cells.length) {
      return;
    }


    const cards = [];


    cells.forEach(cell => {

      /*
       * Real Ella grid item.
       *
       * CSS uses this class to control:
       *
       * Desktop collection gap 10px
       * Tablet gap 8px
       * Mobile gap 5px
       */
      cell.classList.add(
        'kg-grid-cell'
      );


      const card =
        getCardFromCell(cell);


      if (!card) {
        return;
      }


      card.classList.add(
        'kg-card-fixed'
      );


      const media =
        getMediaBox(card);


      getContentBox(card);


      const quick =
        getQuickAdd(card);


      if (
        media &&
        quick
      ) {
        positionQuickAdd(
          card,
          media,
          quick
        );
      }


      cards.push(card);

    });


    equalizeGridRows(
      cards
    );
  };


  /* =========================================================
     REFRESH ALL PRODUCT GRIDS
     ========================================================= */

  const refresh = (
    root = document
  ) => {

    const grids =
      getGrids(root);


    grids.forEach(
      grid => {
        setupGrid(grid);
      }
    );

  };


  /* =========================================================
     QUEUE REFRESH
     ========================================================= */

  const queueRefresh = (
    root = document
  ) => {

    if (
      refreshQueued
    ) {
      return;
    }


    refreshQueued = true;


    requestAnimationFrame(() => {

      requestAnimationFrame(() => {

        refreshQueued = false;

        refresh(root);

      });

    });

  };


  /* =========================================================
     INITIALIZE
     ========================================================= */

  const init = (
    root = document
  ) => {

    initSectionMotion(root);

    queueRefresh(root);

  };


  /* =========================================================
     INITIAL PAGE LOAD
     ========================================================= */

  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      () => {
        init(document);
      },
      {
        once: true
      }
    );

  } else {

    init(document);

  }


  /* =========================================================
     WINDOW LOAD
     ========================================================= */

  window.addEventListener(
    'load',
    () => {

      queueRefresh(
        document
      );

    }
  );


  /* =========================================================
     ELLA LAZY LOADING / SLIDERS
     ========================================================= */

  setTimeout(
    () => {
      queueRefresh(
        document
      );
    },
    400
  );


  setTimeout(
    () => {
      queueRefresh(
        document
      );
    },
    1100
  );


  setTimeout(
    () => {
      queueRefresh(
        document
      );
    },
    2000
  );


  /* =========================================================
     RESPONSIVE RECALCULATION
     ========================================================= */

  window.addEventListener(
    'resize',
    () => {

      clearTimeout(
        resizeTimer
      );


      resizeTimer =
        setTimeout(
          () => {

            queueRefresh(
              document
            );

          },
          160
        );

    }
  );


  /* =========================================================
     SHOPIFY THEME EDITOR SUPPORT
     ========================================================= */

  document.addEventListener(
    'shopify:section:load',
    event => {

      init(
        event.target
      );

    }
  );


  /* =========================================================
     AJAX COLLECTION FILTERS
     DYNAMIC PRODUCT SECTIONS
     ========================================================= */

  const main =
    document.getElementById(
      'MainContent'
    );


  if (
    main &&
    'MutationObserver' in window
  ) {

    const observer =
      new MutationObserver(
        mutations => {

          const added =
            mutations.some(
              mutation =>
                mutation
                  .addedNodes
                  .length
            );


          if (added) {

            queueRefresh(
              main
            );

          }

        }
      );


    observer.observe(
      main,
      {
        childList: true,
        subtree: true
      }
    );

  }

})();